import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiUtils';

function loadBanquestTokenization() {
  return new Promise((resolve, reject) => {
    if (window.HostedTokenization) { resolve(window.HostedTokenization); return; }
    const script = document.createElement('script');
    const base = import.meta.env.VITE_BANQUEST_SANDBOX === 'true'
      ? 'https://tokenization.sandbox.banquestgateway.com'
      : 'https://tokenization.banquestgateway.com';
    script.src = `${base}/tokenization/v0.3`;
    script.async = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.HostedTokenization) { clearInterval(check); resolve(window.HostedTokenization); }
      }, 50);
      setTimeout(() => { clearInterval(check); reject(new Error('Payment library did not initialize')); }, 8000);
    };
    script.onerror = () => reject(new Error('Failed to load payment form. Please refresh and try again.'));
    document.head.appendChild(script);
  });
}

export default function CustomPay() {
  const [searchParams] = useSearchParams();
  const amount = parseFloat(searchParams.get('amount'));
  const desc = searchParams.get('desc') || 'Custom Payment';
  const regs = searchParams.get('regs') || '';
  const adminCode = searchParams.get('code') || '';
  const markPaid = searchParams.get('markPaid') !== 'false';

  const [state, setState] = useState('paymentForm');
  const [cardError, setCardError] = useState('');
  const [tokenizationReady, setTokenizationReady] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const hostedTokenizationRef = useRef(null);
  const initStartedRef = useRef(false);

  const regIds = regs ? regs.split(',').map(s => s.trim()).filter(Boolean) : [];
  const perPerson = regIds.length > 0 ? (amount / regIds.length) : amount;

  // Initialize Banquest Hosted Tokenization
  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const tokenizationKey = import.meta.env.VITE_BANQUEST_TOKENIZATION_KEY;
    if (!tokenizationKey) {
      setCardError('Payment system not configured. Please contact flights@igudys.com.');
      return;
    }

    loadBanquestTokenization()
      .then(HostedTokenization => {
        const fieldStyle = 'border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 16px; height: 44px; box-sizing: border-box; outline: none;';
        const instance = new HostedTokenization(tokenizationKey, {
          target: '#banquest-card-form',
          showZip: true,
          requireCvv2: true,
          styles: {
            container: 'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 16px;',
            card: fieldStyle + ' width: 100%;',
            expiryMonth: fieldStyle + ' width: 70px; text-align: center;',
            expirySeparator: 'font-size: 20px; color: #9ca3af; padding: 0 4px; line-height: 44px;',
            expiryYear: fieldStyle + ' width: 70px; text-align: center;',
            cvv2: fieldStyle + ' width: 80px; text-align: center;',
            avsZip: fieldStyle + ' width: 140px;',
            labelType: 'static-top',
            labels: 'font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; display: block;',
          },
        });

        instance.on('ready', () => setTokenizationReady(true));
        instance.on('input', ({ error }) => {
          if (error) setCardError(error.message || 'Please check your card details');
          else setCardError('');
        });

        hostedTokenizationRef.current = instance;
      })
      .catch(err => setCardError(err.message));

    return () => {
      if (hostedTokenizationRef.current) {
        try { hostedTokenizationRef.current.destroy(); } catch (_) {}
        hostedTokenizationRef.current = null;
      }
    };
  }, []);

  const handlePay = useCallback(async () => {
    if (!tokenizationReady || !hostedTokenizationRef.current || state === 'processing') return;
    setCardError('');

    let result;
    try {
      result = await hostedTokenizationRef.current.getNonceToken();
    } catch (err) {
      setCardError(err?.message || 'Please check your card details and try again.');
      return;
    }

    if (!result?.nonce) {
      setCardError('Could not process card. Please check your details and try again.');
      return;
    }

    setState('processing');

    try {
      const response = await apiFetch(`${import.meta.env.VITE_API_URL}/admin-banquest-charge`, {
        method: 'POST',
        body: JSON.stringify({
          nonce: result.nonce,
          expiryMonth: result.expiryMonth,
          expiryYear: result.expiryYear,
          avsZip: result.avsZip || undefined,
          amount,
          description: desc,
          regIds: regIds.map(Number).filter(n => !isNaN(n)),
          adminCode,
          markPaid,
        }),
      });

      if (response.status === 'success') {
        setPaymentResult(response);
        setState('success');
      } else if (response.status === 'declined') {
        setCardError(response.message || 'Payment declined. Please try a different card.');
        setState('paymentForm');
      } else {
        setCardError(response.message || 'Payment failed. Please try again.');
        setState('paymentForm');
      }
    } catch (err) {
      setCardError(err.message || 'Network error. Please try again.');
      setState('paymentForm');
    }
  }, [tokenizationReady, state, amount, desc, regIds, adminCode, markPaid]);

  if (!amount || amount <= 0 || !adminCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-4xl mb-4">&#9888;&#65039;</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Invalid Payment Link</h2>
          <p className="text-gray-600">Missing required parameters (amount, code).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-700 px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src="/igud-logo.png" alt="Igud Yeshivos" className="h-10 w-auto" />
            <img src="/ehk-logo_2-1.png" alt="Eretz Hakodesh" className="h-10 w-auto" />
          </div>
          <h1 className="text-white font-bold text-xl">Charter Flight Payment</h1>
        </div>

        <div className="px-6 py-8">
          {state === 'paymentForm' && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">{desc}</h2>
                <p className="text-3xl font-bold text-gray-800">${amount.toLocaleString()}</p>
                {regIds.length > 1 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {regIds.length} registrations &middot; ${perPerson.toLocaleString()} each
                  </p>
                )}
              </div>

              <style>{`
                #banquest-card-form iframe {
                  width: 100% !important;
                  min-height: 380px !important;
                  border: none !important;
                }
              `}</style>
              <div id="banquest-card-form" className="mb-6"></div>

              {cardError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
                  {cardError}
                </div>
              )}

              <button
                onClick={handlePay}
                disabled={!tokenizationReady}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  tokenizationReady
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {tokenizationReady ? `Pay $${amount.toLocaleString()}` : 'Loading payment form...'}
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mt-4 text-xs text-gray-500 text-center">
                <p className="mb-1">The charge on your statement will appear from:</p>
                <p className="font-semibold text-gray-700">THE COORDINATING COUNCIL FOR JEWISH HOMELAND (CCJH)</p>
                <p className="mt-1">EIN 26-1320314</p>
              </div>

              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secure payment — your card details are encrypted</span>
              </div>
            </div>
          )}

          {state === 'processing' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Processing payment...</h2>
              <p className="text-gray-500 text-sm">Please do not close this page.</p>
            </div>
          )}

          {state === 'success' && (
            <div className="text-center">
              <div className="text-5xl mb-4">&#10003;</div>
              <h2 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-4">{desc}</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-700 mb-1">Amount charged:</p>
                <p className="text-2xl font-bold text-green-900">${amount.toLocaleString()}</p>
              </div>
              {paymentResult?.last4 && (
                <p className="text-sm text-gray-500">
                  Charged to {paymentResult.cardType || 'card'} ending in {paymentResult.last4}
                </p>
              )}
              {paymentResult?.referenceNumber && (
                <p className="text-sm text-gray-400 mt-1">
                  Ref: {paymentResult.referenceNumber}
                </p>
              )}
              {paymentResult?.regsUpdated > 0 && (
                <p className="text-sm text-green-600 mt-3">
                  {paymentResult.regsUpdated} registration{paymentResult.regsUpdated > 1 ? 's' : ''} marked as paid
                </p>
              )}
              <p className="text-gray-500 text-sm mt-4">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
